import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, XCircle, Loader } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { authAPI } from '../api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useContext(AuthContext);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Charger les infos de l'invitation (endpoint public)
  useEffect(() => {
    fetch(`${API_URL}/team-management/invitations/${token}/preview`)
      .then(r => r.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setPreview(data);
      })
      .catch(() => setError("Impossible de charger l'invitation"))
      .finally(() => setLoading(false));
  }, [token]);

  // Si non connecté et invitation valide → sauvegarder le token et rediriger vers login
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !loading && !error) {
      sessionStorage.setItem('pendingInviteToken', token);
      navigate('/riot-login');
    }
  }, [authLoading, isAuthenticated, loading, error, token, navigate]);

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await authAPI.acceptInvitation(token);
      setSuccess(`Tu as rejoint l'équipe ${preview?.team?.name} ! 🎉`);
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    try {
      await authAPI.declineInvitation(token);
      setSuccess('Invitation refusée.');
      setTimeout(() => navigate('/'), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '0.75rem' }}>
        <Loader size={24} style={{ color: '#a46bff', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#9ca3af' }}>Chargement de l'invitation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem' }}>
        <p style={{ color: '#ef4444', fontSize: '1.1rem' }}>❌ {error}</p>
        <button className="submit-button" style={{ maxWidth: 240 }} onClick={() => navigate('/')}>
          Retour à l'accueil
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem' }}>
        <CheckCircle size={48} style={{ color: '#10b981' }} />
        <p style={{ color: '#10b981', fontSize: '1.2rem', fontWeight: 600 }}>{success}</p>
      </div>
    );
  }

  if (!preview || !isAuthenticated) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="auth-page">
      <div className="auth-container">
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="auth-card"
          style={{ textAlign: 'center' }}
        >
          <Users size={48} style={{ color: '#a46bff', marginBottom: '1rem' }} />
          <h2 style={{ color: '#e8e6f3', marginBottom: '0.5rem' }}>Invitation d'équipe</h2>
          <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
            <strong style={{ color: '#a46bff' }}>{preview.inviterName}</strong> t'invite à rejoindre
          </p>

          <div style={{
            background: '#1a1030',
            border: '1px solid #2a1f44',
            borderRadius: 12,
            padding: '1.25rem',
            marginBottom: '2rem'
          }}>
            {preview.team.logo && (
              <img
                src={preview.team.logo}
                alt={preview.team.name}
                style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', marginBottom: '0.75rem' }}
              />
            )}
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e8e6f3', margin: 0 }}>
              {preview.team.name}
            </p>
            {preview.team.tag && (
              <p style={{ color: '#a46bff', margin: '4px 0 0' }}>[{preview.team.tag}]</p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              className="submit-button"
              style={{ background: '#10b981', minWidth: 130, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
              onClick={handleAccept}
              disabled={actionLoading}
            >
              <CheckCircle size={16} /> Accepter
            </button>
            <button
              className="submit-button"
              style={{ background: '#ef4444', minWidth: 130, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
              onClick={handleDecline}
              disabled={actionLoading}
            >
              <XCircle size={16} /> Refuser
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
